import React, { type RefObject, useContext, useState } from 'react';
import { useMedia } from 'react-use';
import { useDietaryRestrictions } from '@hungryroot/dietary-restrictions';
import { useScrollToTopFixIOS } from '@hungryroot/environment';
import { type DietaryRestrictionConfig } from '@hungryroot/rest/api/v2/dietary-restrictions/get/get-dietary-restrictions';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import { ControlSelect } from '../../components/ControlSelect/ControlSelect';
import { QuizQuestionScreen } from '../../components/QuizQuestionScreen/QuizQuestionScreen';
import { SectionLabel } from '../../components/SectionLabel/SectionLabel';
import { ToggleVariant, Toggle } from '../../components/Toggle/Toggle/Toggle';
import { Dietary, type Quiz } from '../../defs/quiz';
import { QuizContext } from '../../quiz-context';
import {
  tagAnalytics,
  type QuizPreferenceItem,
} from '../../segment/quizAnalytics';
import styles from './EatingHabitsDietaryNeeds.module.css';
import layoutStyles from '../layout.module.css';

/** dairy-free -> Dairy-Free */
const toKebabTitleCase = (str: string) =>
  str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('-');

interface DietaryToggleConfig {
  dietaryType: Dietary;
  image: React.ReactNode;
  label?: string;
}

interface DietaryToggleProps {
  dietary: Quiz['dietary'];
  testid: string;
  isDesktop: boolean;
  config: DietaryToggleConfig;
  toggleVariant: ToggleVariant;
}

const DietaryToggle = ({
  dietary,
  testid,
  isDesktop,
  config,
  toggleVariant,
}: DietaryToggleProps) => (
  <Toggle
    active={dietary === null ? false : dietary.includes(config.dietaryType)}
    testid={`${testid}-toggle-${config.dietaryType}`}
    label={config.label ?? toKebabTitleCase(config.dietaryType)}
    variant={toggleVariant}
    illustration={config.image}
    className={
      isDesktop
        ? layoutStyles['animation-delay-point-65s']
        : layoutStyles['animation-delay-point-8s']
    }
  />
);

type DietaryToggleDisplayConfig = {
  dietaryType: Dietary;
  label: string;
  image: JSX.Element | null;
};

/** All the dietary restrictions we want to render on this page */
const dietaryRestrictionsToDisplay: Dietary[] = [
  Dietary.DairyFree,
  Dietary.GlutenFree,
  Dietary.SoyFree,
  Dietary.TreeNutFree,
  Dietary.PeanutFree,
  Dietary.EggFree,
  Dietary.ShellfishFree,
  Dietary.SesameFree,
  Dietary.GrainFree,
  Dietary.GarlicFree,
  Dietary.OnionFree,
  Dietary.CaffeineFree,
] as const;

const generateDietaryDisplayConfigs = (
  configsFromNetwork: DietaryRestrictionConfig[] | undefined
): DietaryToggleDisplayConfig[] => {
  if (!configsFromNetwork) return [];
  return dietaryRestrictionsToDisplay
    .map((dietaryType) => {
      const networkConfig = configsFromNetwork?.find(
        (config) => config.slug === dietaryType
      );

      if (!networkConfig) return null;

      const image =
        networkConfig && networkConfig.thumbnail_url ? (
          <img
            src={networkConfig.thumbnail_url}
            alt={networkConfig.name}
            className={styles.icon}
          />
        ) : null;

      return {
        dietaryType,
        label: networkConfig?.name,
        image,
      };
    })
    .filter(Boolean) as DietaryToggleDisplayConfig[];
};

interface Props {
  testid: string;
  scrollRef: RefObject<HTMLDivElement>;
}

export function EatingHabitsDietaryNeeds(props: Props) {
  const { testid, scrollRef } = props;
  const { quiz } = useContext(QuizContext);
  const [trackedItems, setTrackedItems] = useState<QuizPreferenceItem[]>([]);

  const { dietary } = quiz;

  const isDesktop = useMedia(BreakpointQueryLarge);

  useScrollToTopFixIOS(scrollRef);

  const dietaryRestrictionConfigs = useDietaryRestrictions();

  const displayConfigs = generateDietaryDisplayConfigs(
    dietaryRestrictionConfigs.data?.results
  );
  const items = displayConfigs.map((config) => ({
    value: config.dietaryType,
    label: config.label,
  }));
  return (
    <QuizQuestionScreen
      testid={testid}
      heroText="Do you have any dietary needs?"
      sectionLabel={<SectionLabel label="About you" />}
      onSubmitAnalytics={() => {
        tagAnalytics('Quiz Dietary Needs', trackedItems);
      }}
    >
      <ControlSelect
        ariaLabel="Select your dietary needs"
        defaultValue={[]}
        question="dietary"
        layoutVariant="two-column"
        items={items}
        selectedKeys={dietary === null ? [] : dietary}
        onTrackSelected={(items: QuizPreferenceItem[]) => {
          setTrackedItems(items);
        }}
      >
        {displayConfigs.map((config) => (
          <ControlSelect.Option
            key={config.dietaryType}
            id={config.dietaryType}
            label={config.dietaryType}
          >
            <DietaryToggle
              dietary={dietary}
              testid={testid}
              isDesktop={isDesktop}
              config={config}
              toggleVariant={ToggleVariant.One}
            />
          </ControlSelect.Option>
        ))}
      </ControlSelect>
    </QuizQuestionScreen>
  );
}
